import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Typography, Button, Row, Col, Card, Spin, Carousel } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { storefrontApi, type ProductDetail as ProductDetailType } from '@/apis/storefrontApi';
import styles from './index.module.less';

const { Title, Paragraph } = Typography;

function toImageList(value: string | string[] | null | undefined): string[] {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

/** 加载图片得到自然尺寸，在给定宽度下计算显示高度 */
function loadImageHeights(sources: string[], containerWidth: number): Promise<number[]> {
  return Promise.all(
    sources.map(
      (src) =>
        new Promise<number>((resolve) => {
          const img = new Image();
          img.onload = () => {
            if (containerWidth <= 0) {
              resolve(0);
              return;
            }
            const h = (img.naturalHeight * containerWidth) / img.naturalWidth;
            resolve(h);
          };
          img.onerror = () => resolve(0);
          img.src = src;
        })
    )
  );
}

const ProductDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const listSearch = (location.state as { listSearch?: string } | null)?.listSearch;
  const backTo = listSearch ? `/products?${listSearch}` : '/products';
  const [product, setProduct] = useState<ProductDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const mediaRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const [mediaHeight, setMediaHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!id) return;
    storefrontApi
      .getProductDetail(id)
      .then(setProduct)
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  const mainImages = useMemo(
    () => (product ? toImageList(product.image) : []),
    [product?.image],
  );
  const introImages = product?.images ?? [];

  const updateMediaHeight = useCallback(() => {
    const mediaEl = mediaRef.current;
    const rightEl = rightRef.current;
    if (!mediaEl || !rightEl) return;
    const mediaWidth = mediaEl.offsetWidth;
    const rightHeight = rightEl.offsetHeight;
    if (mediaWidth <= 0 || rightHeight <= 0) return;

    if (mainImages.length === 0) {
      setMediaHeight(Math.min(mediaWidth * (3 / 4), rightHeight));
      return;
    }

    loadImageHeights(mainImages, mediaWidth).then((heights) => {
      const maxH = Math.max(0, ...heights);
      const capped = Math.min(maxH, rightHeight);
      setMediaHeight(capped);
    });
  }, [mainImages]);

  useEffect(() => {
    if (!product) return;
    updateMediaHeight();
    const mediaEl = mediaRef.current;
    const rightEl = rightRef.current;
    if (!mediaEl || !rightEl) return;
    const ro = new ResizeObserver(updateMediaHeight);
    ro.observe(mediaEl);
    ro.observe(rightEl);
    return () => ro.disconnect();
  }, [product, mainImages, updateMediaHeight]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingWrap}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <Paragraph>{t('products.notFound')}</Paragraph>
          <Link to={backTo}>{t('products.backToProducts')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <Link to={backTo} className={styles.back}>
          <ArrowLeftOutlined /> {t('products.backToProducts')}
        </Link>

        <Row gutter={[32, 24]}>
          <Col xs={24} md={12}>
            <div
              ref={mediaRef}
              className={styles.media}
              style={mediaHeight != null ? { height: mediaHeight, aspectRatio: 'unset' } : undefined}
            >
              {mainImages.length > 0 ? (
                mainImages.length === 1 ? (
                  <img
                    src={mainImages[0]}
                    alt={product.name}
                    className={styles.mediaImg}
                  />
                ) : (
                  <Carousel className={styles.carousel} autoplay effect="fade" dots>
                    {mainImages.map((src, i) => (
                      <div key={i} className={styles.carouselItem}>
                        <img src={src} alt={`${product.name} ${i + 1}`} className={styles.mediaImg} />
                      </div>
                    ))}
                  </Carousel>
                )
              ) : (
                <div className={styles.mediaPlaceholder}>{t('products.productImage')}</div>
              )}
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div ref={rightRef} className={styles.rightContent}>
            <Title level={2} className={styles.title}>
              {product.name}
            </Title>
            <Paragraph className={styles.price}>${product.price}</Paragraph>
            <Paragraph className={styles.summary}>{product.summary}</Paragraph>
            <Paragraph className={styles.desc}>{product.description}</Paragraph>

            {product.specs?.length > 0 && (
              <Card size="small" title={t('products.specifications')} className={styles.specs}>
                {product.specs.map((s) => (
                  <Row key={s.label} className={styles.specRow}>
                    <Col span={10}>{s.label}</Col>
                    <Col span={14}>{s.value}</Col>
                  </Row>
                ))}
              </Card>
            )}

            <div className={styles.actions}>
              <Button type="primary" size="large">
                {t('products.requestQuote')}
              </Button>
              <Link to="/contact">
                <Button size="large">{t('products.contactUs')}</Button>
              </Link>
            </div>
            </div>
          </Col>
        </Row>

        {/* 内容区：介绍图优先展示，下方为富文本 */}
        {(introImages.length > 0 || product.content) && (
          <div className={styles.contentSection}>
            {introImages.length > 0 && (
              <div className={styles.introImages}>
                {introImages.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`${product.name} intro ${i + 1}`}
                    className={styles.introImg}
                  />
                ))}
              </div>
            )}
            {product.content && (
              <div
                className={styles.richContent}
                dangerouslySetInnerHTML={{ __html: product.content }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
